/**
 * ============================================================================
 * ReviewMinutes — Phase 7 frontend (Management Review Module).
 * Owner: Phase 7 — Management Review Module (Agent F).
 * Route: /management-reviews/:id
 * Purpose: full minutes view for one Management Review:
 *   - Left column: meta (chair, period, attendees), KPI cards grid, action
 *     items list with CAPA links.
 *   - Right column: attendee chips (with mandatory-category highlight),
 *     finalize-minutes panel (ExecutiveSponsor sign), cadence info.
 *
 * UX RULES:
 *   - Skeletons during load, refined error envelope on failure.
 *   - KPI cards with critical-coloring when count > 0 on critical metrics.
 *   - Action items show their linked CAPA id when createsCapa=true.
 *
 * URS rows: MGR-001..008.
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './ReviewMinutes.module.css';
import { StatusBadge } from '../ReviewCalendar/StatusBadge';
import { KpiCard } from './KpiCard';
import { ActionItemRow } from './ActionItemRow';
import { useReviewDetailQuery } from './useReviewDetailQuery';

const MANDATORY = ['QA_LEADERSHIP', 'OPERATIONS', 'MANUFACTURING'];

export interface ReviewMinutesProps {
  /** Review id (passed by the router; default works for the demo seed). */
  reviewId?: string;
  /** Optional finalize handler. */
  onFinalize?: (reviewId: string) => void;
  /** Optional close-action handler. */
  onCloseAction?: (actionId: string) => void;
}

export function ReviewMinutes({
  reviewId = 'mr-demo',
  onFinalize,
  onCloseAction,
}: ReviewMinutesProps = {}) {
  const { t, i18n } = useTranslation();
  const q = useReviewDetailQuery(reviewId);
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );

  if (q.isLoading) {
    return (
      <div className={wstyles.adminScreen}>
        <Card>
          <CardBody>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className={styles.skeletonRow} aria-hidden="true" />
            ))}
          </CardBody>
        </Card>
      </div>
    );
  }

  if (q.isError || !q.data) {
    return (
      <div className={wstyles.adminScreen}>
        <Card>
          <CardBody>
            <h2>
              {t('mgr.minutes.error.title', {
                defaultValue: 'Unable to load review',
              })}
            </h2>
            <Button variant="secondary" onClick={() => q.refetch()}>
              {t('mgr.minutes.retry', { defaultValue: 'Retry' })}
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  const r = q.data;
  const snap = r.kpiSnapshot;
  const presentCategories = new Set(r.attendeeCategoriesPresent ?? []);

  const canFinalize =
    onFinalize &&
    (r.status === 'HELD' || r.status === 'MINUTES_DRAFT');

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('mgr.minutes.eyebrow', { defaultValue: 'Quality Events' })}
        title={
          <>
            {r.title} <StatusBadge status={r.status} />
          </>
        }
        subtitle={
          <>
            <span className={styles.reviewNumber}>{r.reviewNumber}</span> ·{' '}
            {t('mgr.minutes.period', { defaultValue: 'Coverage' })}:{' '}
            {dateFmt.format(new Date(r.periodStartUtc))} →{' '}
            {dateFmt.format(new Date(r.periodEndUtc))}
          </>
        }
        actions={
          canFinalize ? (
            <Button variant="primary" onClick={() => onFinalize?.(r.id)}>
              {t('mgr.minutes.actions.finalize', {
                defaultValue: 'Finalize minutes',
              })}
            </Button>
          ) : undefined
        }
      />

      <div className={styles.minutesLayout}>
        <div className={styles.agendaCard}>
          <div className={styles.metaRow}>
            <div className={styles.metaItem}>
              <strong>{t('mgr.minutes.scheduled', { defaultValue: 'Scheduled' })}</strong>
              {dateFmt.format(new Date(r.scheduledAtUtc))}
            </div>
            {r.heldAtUtc && (
              <div className={styles.metaItem}>
                <strong>{t('mgr.minutes.held', { defaultValue: 'Held' })}</strong>
                {dateFmt.format(new Date(r.heldAtUtc))}
              </div>
            )}
            <div className={styles.metaItem}>
              <strong>{t('mgr.minutes.chair', { defaultValue: 'Chair' })}</strong>
              {r.chairUserId.slice(0, 8)}…
            </div>
            <div className={styles.metaItem}>
              <strong>{t('mgr.minutes.attendees', { defaultValue: 'Attendees' })}</strong>
              {r.attendeeCount}
            </div>
            {r.minutesSignedAtUtc && (
              <div className={styles.metaItem}>
                <strong>
                  {t('mgr.minutes.signedAt', { defaultValue: 'Signed at' })}
                </strong>
                {dateFmt.format(new Date(r.minutesSignedAtUtc))}
              </div>
            )}
          </div>

          {snap && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>
                {t('mgr.minutes.kpi.title', { defaultValue: 'KPI snapshot' })}
              </h2>
              <div className={styles.kpiGrid}>
                <KpiCard
                  label={t('mgr.kpi.capa_open', { defaultValue: 'CAPA Open' })}
                  value={snap.capaOpenCount}
                />
                <KpiCard
                  label={t('mgr.kpi.capa_overdue', { defaultValue: 'CAPA Overdue' })}
                  value={snap.capaOverdueCount}
                  isCritical={snap.capaOverdueCount > 0}
                />
                <KpiCard
                  label={t('mgr.kpi.dev_critical', { defaultValue: 'Deviation Critical' })}
                  value={snap.deviationCriticalCount}
                  isCritical={snap.deviationCriticalCount > 0}
                />
                <KpiCard
                  label={t('mgr.kpi.dev_major', { defaultValue: 'Deviation Major' })}
                  value={snap.deviationMajorCount}
                />
                <KpiCard
                  label={t('mgr.kpi.oos_phase2', { defaultValue: 'OOS Phase 2' })}
                  value={snap.oosPhase2OpenCount}
                />
                <KpiCard
                  label={t('mgr.kpi.supplier_disq', { defaultValue: 'Supplier Disqualified' })}
                  value={snap.supplierDisqualifiedCount}
                />
                <KpiCard
                  label={t('mgr.kpi.eq_restricted', { defaultValue: 'Equipment Restricted' })}
                  value={snap.equipmentRestrictedCount}
                />
                <KpiCard
                  label={t('mgr.kpi.training_overdue', { defaultValue: 'Training Overdue' })}
                  value={snap.trainingOverdueCount}
                />
                <KpiCard
                  label={t('mgr.kpi.audit_findings', { defaultValue: 'Audit Findings' })}
                  value={snap.auditFindingsOpenCount}
                />
                <KpiCard
                  label={t('mgr.kpi.risk_critical', { defaultValue: 'Risk Critical' })}
                  value={snap.riskCriticalCount}
                  isCritical={snap.riskCriticalCount > 0}
                />
              </div>
            </section>
          )}

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {t('mgr.minutes.actions.title', { defaultValue: 'Action items' })}{' '}
              <span className={styles.reviewNumber}>({r.actionItems.length})</span>
            </h2>
            {r.actionItems.length === 0 ? (
              <p className={styles.actionMeta}>
                {t('mgr.minutes.actions.empty', {
                  defaultValue:
                    'No actions raised yet. Use “Add action” on the meeting page.',
                })}
              </p>
            ) : (
              <ul className={styles.actionsList}>
                {r.actionItems.map((a) => (
                  <ActionItemRow key={a.id} action={a} onClose={onCloseAction} />
                ))}
              </ul>
            )}
          </section>
        </div>

        <aside className={styles.sidebar}>
          <Card className={styles.sidebarCard}>
            <CardHeader>
              <h3 className={styles.sectionTitle}>
                {t('mgr.minutes.attendees.title', { defaultValue: 'Attendees' })}
              </h3>
            </CardHeader>
            <CardBody>
              <p className={styles.actionMeta}>
                {t('mgr.minutes.attendees.mandatory', {
                  defaultValue:
                    'Mandatory categories (MGR-003): QA Leadership, Operations, Manufacturing.',
                })}
              </p>
              <div>
                {MANDATORY.map((cat) => (
                  <span
                    key={cat}
                    className={
                      presentCategories.has(cat)
                        ? `${styles.attendeeChip} ${styles.attendeeRequired}`
                        : `${styles.attendeeChip} ${styles.attendeeMissing}`
                    }
                  >
                    {cat}
                    {presentCategories.has(cat) ? ' ✓' : ' ✗'}
                  </span>
                ))}
                {(r.attendeeCategoriesPresent ?? [])
                  .filter((c) => !MANDATORY.includes(c))
                  .map((c) => (
                    <span key={c} className={styles.attendeeChip}>
                      {c}
                    </span>
                  ))}
              </div>
            </CardBody>
          </Card>

          {r.minutesDocId && (
            <Card className={styles.sidebarCard}>
              <CardHeader>
                <h3 className={styles.sectionTitle}>
                  {t('mgr.minutes.doc.title', { defaultValue: 'Minutes document' })}
                </h3>
              </CardHeader>
              <CardBody>
                <p className={styles.actionMeta}>{r.minutesDocId}</p>
                {r.minutesSignerUserId && (
                  <p className={styles.actionMeta}>
                    {t('mgr.minutes.signedBy', { defaultValue: 'Signed by' })}:{' '}
                    {r.minutesSignerUserId.slice(0, 8)}…
                  </p>
                )}
              </CardBody>
            </Card>
          )}
        </aside>
      </div>
    </div>
  );
}
